import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import classnames from 'classnames';
import ErrorBoundary from '@sbt-web/error-boundary';
import { UserRating } from '@/client/components/Detail/UserRating';
import type { ReviewFlow, ReviewRole } from '@/types';
import { ReviewInfo } from './ReviewInfo';
import { ReviewText } from './ReviewText';
import ownClasses from './index.module.scss';
interface CommonProps {
  userId: string;
  classes?: string[];
  isAutomaticReview?: boolean;
  text?: string;
  score?: number;
  reviewCount?: number;
}
interface PreviewProps {
  onClick: () => void;
  isPreview: boolean;
  userName?: never;
  reviewerName?: never;
  date?: never;
  reviewerRole?: never;
  flow?: never;
  itemTitle?: never;
  reviewerProfileUrl?: never;
}
interface NoPreviewProps {
  userName: string;
  reviewerName: string;
  date: string;
  reviewerRole: ReviewRole;
  flow: ReviewFlow;
  isPreview?: never;
  onClick?: never;
  itemTitle?: string;
  reviewerProfileUrl?: string;
}
type TextualReviewProps = CommonProps & (PreviewProps | NoPreviewProps);
type WrapperProps = Pick<TextualReviewProps, 'isPreview' | 'classes' | 'onClick'> & {
  children: ReactNode;
};
const AUTOMATIC_REVIEW_RATING_COLOR = '#707e8f';
const SHOW_REVIEW_TEXT = 'Vedi tutte le recensioni';
const ShowReview = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <p className={classnames(ownClasses.show_review_button, "body-text", "small", "semibold")}>{SHOW_REVIEW_TEXT}</p>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const Wrapper = t0 => {
  const $ = _c(10);
  const {
    children,
    isPreview,
    classes,
    onClick
  } = t0;
  let t1;
  if ($[0] !== classes || $[1] !== isPreview) {
    t1 = classnames(ownClasses.wrapper, {
      [ownClasses.preview]: isPreview
    }, classes);
    $[0] = classes;
    $[1] = isPreview;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const wrapperClasses = t1;
  let t2;
  if ($[3] !== children || $[4] !== onClick || $[5] !== wrapperClasses) {
    t2 = <button className={wrapperClasses} onClick={onClick} aria-label={SHOW_REVIEW_TEXT}>{children}</button>;
    $[3] = children;
    $[4] = onClick;
    $[5] = wrapperClasses;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const previewWrapper = t2;
  let t3;
  if ($[7] !== children || $[8] !== wrapperClasses) {
    t3 = <div className={wrapperClasses}>{children}</div>;
    $[7] = children;
    $[8] = wrapperClasses;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const wrapper = t3;
  return isPreview ? previewWrapper : wrapper;
};
const TextualReviewComponent = t0 => {
  const $ = _c(28);
  const {
    classes,
    date,
    flow,
    reviewerName,
    reviewerRole,
    userName,
    itemTitle,
    isAutomaticReview,
    text,
    isPreview,
    onClick,
    userId,
    score,
    reviewCount,
    reviewerProfileUrl
  } = t0;
  const ratingColor = isAutomaticReview ? AUTOMATIC_REVIEW_RATING_COLOR : undefined;
  const t1 = !isPreview;
  let t2;
  if ($[0] !== ratingColor || $[1] !== reviewCount || $[2] !== score || $[3] !== t1 || $[4] !== userId) {
    t2 = <UserRating userId={userId} score={score} starColor={ratingColor} reviewCount={reviewCount} showScore={t1} showRating={true} />;
    $[0] = ratingColor;
    $[1] = reviewCount;
    $[2] = score;
    $[3] = t1;
    $[4] = userId;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== isAutomaticReview || $[7] !== isPreview || $[8] !== text) {
    t3 = <ReviewText isAutomaticReview={isAutomaticReview} text={text} isPreview={isPreview} />;
    $[6] = isAutomaticReview;
    $[7] = isPreview;
    $[8] = text;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== date || $[11] !== flow || $[12] !== isAutomaticReview || $[13] !== isPreview || $[14] !== itemTitle || $[15] !== reviewerName || $[16] !== reviewerProfileUrl || $[17] !== reviewerRole || $[18] !== userId || $[19] !== userName) {
    t4 = isPreview ? <ShowReview /> : <ReviewInfo date={date ?? ""} flow={flow ?? "TUTTOSUBITO"} reviewerName={reviewerName ?? ""} reviewerRole={reviewerRole ?? "BUYER"} userName={userName ?? ""} itemTitle={itemTitle} userId={userId} isAutomaticReview={isAutomaticReview} reviewerProfileUrl={reviewerProfileUrl} />;
    $[10] = date;
    $[11] = flow;
    $[12] = isAutomaticReview;
    $[13] = isPreview;
    $[14] = itemTitle;
    $[15] = reviewerName;
    $[16] = reviewerProfileUrl;
    $[17] = reviewerRole;
    $[18] = userId;
    $[19] = userName;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== classes || $[22] !== isPreview || $[23] !== onClick || $[24] !== t2 || $[25] !== t3 || $[26] !== t4) {
    t5 = <Wrapper classes={classes} isPreview={isPreview} onClick={onClick}>{t2}{t3}{t4}</Wrapper>;
    $[21] = classes;
    $[22] = isPreview;
    $[23] = onClick;
    $[24] = t2;
    $[25] = t3;
    $[26] = t4;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  return t5;
};
export const TextualReview = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <ErrorBoundary><TextualReviewComponent {...props} /></ErrorBoundary>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};